import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { FlagNZ } from "@/components/Flags";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_672445ef-2841-43a6-974f-5ce13dca642e/artifacts/mkkzh5ga_e29fbeb6-4e6b-4cb4-b2f9-06ccc88daf69.jpg";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    // Local-only capture for now — no backend wiring requested
    try {
      const key = "sf_nz_waitlist";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      if (!existing.includes(trimmed)) existing.push(trimmed);
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {
      /* ignore */
    }
    setSubmitted(true);
  };

  return (
    <main
      data-testid="coming-soon-page"
      className="relative min-h-screen overflow-hidden bg-white"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid bg-grid-fade" />

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 sm:px-10 sm:py-8">
        <Link to="/" data-testid="coming-soon-logo-link" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="SimplyFunded"
            className="h-10 w-auto sm:h-11"
            draggable={false}
          />
        </Link>
        <Link
          data-testid="back-home-link"
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-[color:var(--sf-line)] bg-white px-4 py-2 text-sm font-medium text-[color:var(--sf-ink)] transition hover:border-[color:var(--sf-blue)] hover:text-[color:var(--sf-blue)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to regions
        </Link>
      </header>

      <section className="relative z-10 mx-auto max-w-3xl px-6 pb-20 pt-12 text-center sm:px-10 sm:pt-20">
        <div className="rise inline-flex items-center gap-3 rounded-full border border-[color:var(--sf-line)] bg-white/80 px-4 py-2 backdrop-blur">
          <FlagNZ className="h-5 w-8" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--sf-blue)]">
            New Zealand &mdash; Launching Q4 2026
          </span>
        </div>

        <h1
          data-testid="coming-soon-title"
          className="font-display rise mt-8 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          style={{ animationDelay: "0.05s" }}
        >
          Kiwi businesses,
          <br />
          <span className="text-[color:var(--sf-blue)]">we&rsquo;re on our way.</span>
        </h1>

        <p
          className="rise mx-auto mt-6 max-w-xl text-base text-[color:var(--sf-mute)] sm:text-lg"
          style={{ animationDelay: "0.12s" }}
        >
          SimplyFunded is bringing fast, transparent short‑term business loans to
          New Zealand SMEs in Q4 2026. Drop your email and we&apos;ll let you know the
          moment we go live.
        </p>

        {!submitted ? (
          <form
            data-testid="waitlist-form"
            onSubmit={handleSubmit}
            className="rise mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row"
            style={{ animationDelay: "0.18s" }}
            noValidate
          >
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--sf-mute)]" />
              <input
                data-testid="waitlist-email-input"
                type="email"
                required
                placeholder="you@yourbusiness.co.nz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 w-full rounded-full border border-[color:var(--sf-line)] bg-white pl-11 pr-4 text-sm text-[color:var(--sf-ink)] outline-none transition placeholder:text-[color:var(--sf-mute)] focus:border-[color:var(--sf-blue)] focus:ring-4 focus:ring-[color:var(--sf-blue-soft)]"
              />
            </div>
            <button
              data-testid="waitlist-submit-button"
              type="submit"
              className="h-12 rounded-full bg-[color:var(--sf-ink)] px-6 text-sm font-semibold text-white transition hover:bg-[color:var(--sf-blue)]"
            >
              Notify me
            </button>
          </form>
        ) : (
          <div
            data-testid="waitlist-success"
            className="rise mx-auto mt-10 inline-flex items-center gap-3 rounded-2xl border border-[color:var(--sf-blue)] bg-[color:var(--sf-blue-soft)] px-5 py-4 text-left"
          >
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[color:var(--sf-blue)]" />
            <div>
              <p className="text-sm font-semibold text-[color:var(--sf-ink)]">
                You&rsquo;re on the list.
              </p>
              <p className="text-xs text-[color:var(--sf-mute)]">
                We&rsquo;ll be in touch the moment SimplyFunded NZ goes live.
              </p>
            </div>
          </div>
        )}

        {error && (
          <p
            data-testid="waitlist-error"
            className="mt-3 text-sm font-medium text-red-600"
          >
            {error}
          </p>
        )}

        <p className="mt-12 text-xs text-[color:var(--sf-mute)]">
          In the meantime, explore our live regions:&nbsp;
          <a
            href="https://www.simplyfunded.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[color:var(--sf-ink)] underline-offset-4 hover:text-[color:var(--sf-blue)] hover:underline"
          >
            United Kingdom
          </a>
          &nbsp;·&nbsp;
          <a
            href="https://simplyfunded.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[color:var(--sf-ink)] underline-offset-4 hover:text-[color:var(--sf-blue)] hover:underline"
          >
            Australia
          </a>
        </p>
      </section>
    </main>
  );
}
